import { useAuth0 } from "@auth0/auth0-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Col, ConfigProvider, Grid, Row } from "antd";
import React, { lazy, useCallback } from "react";
import { createUseStyles } from "react-jss";
import { AuthContextProvider } from "../core/contexts/AuthContextProvider.jsx";
import { MetaContextProvider } from "../core/contexts/MetaContextProvider";
import { NotificationContextProvider } from "../core/contexts/NotificationContextProvider";
import globalStyle from "../globalStyles";
import { _THEME, mainColor3 } from "../theme";
import MobileNotice from "../mobileNotice/mobileNotice.component.jsx";
import useIsMobile from "./hooks/useIsMobile";
import ContentWrapper from "./contentWrapper";
import Loader from "./loader.jsx";
import { _STYLES } from "./utils/customStyles";

const { useBreakpoint } = Grid;

const MinimalLogin = lazy(() => import("../login/login.jsx"));

const { mainHeader, bodyWrap } = _STYLES;
const useStyles = createUseStyles({
  mainHeader,
  bodyWrap,
});

const Layout = ({ children }) => {
  const classes = useStyles();
  globalStyle();
  const isMobile = useIsMobile();

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchInterval: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        retry: false,
      },
    },
  });

  const screens = useBreakpoint();

  const { isAuthenticated, isLoading } = useAuth0();

  const getResponsiveFontSize = useCallback(() => {
    if (screens.lg) {
      return 14;
    }
    if (screens.md || screens.sm) {
      return 14;
    }
    if (screens.xs) {
      return 12;
    }

    return 14;
  }, [screens.lg, screens.md, screens.sm, screens.xs]);

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: _THEME.color.primary.main,
        },
        components: {
          Button: {
            colorLink: _THEME.color.primary.main,
            fontSizeLG: getResponsiveFontSize(),
            fontSize: getResponsiveFontSize(),
            fontSizeSM: getResponsiveFontSize(),
            fontSizeXL: getResponsiveFontSize(),
          },
          Typography: {
            colorPrimary: _THEME.color.text.main,
          },
          Table: {
            headerBorderRadius: 16,
            borderColor: mainColor3,
            cellFontSize: _THEME.size.sm[3],
            fontSizeLG: getResponsiveFontSize(),
            fontSize: getResponsiveFontSize(),
            fontSizeSM: getResponsiveFontSize(),
            fontSizeXL: getResponsiveFontSize(),
          },
          Segmented: {
            itemSelectedBg: _THEME.color.primary.main,
            itemSelectedColor: _THEME.color.white,
            //trackBg: _THEME.color.grey.accent6,
            itemColor: _THEME.color.text.main,
            fontSizeLG: getResponsiveFontSize(),
            fontSize: getResponsiveFontSize(),
            fontSizeSM: getResponsiveFontSize(),
            fontSizeXL: getResponsiveFontSize(),
          },
          Form: {
            labelFontSize: getResponsiveFontSize(),
            itemMarginBottom: screens.lg ? 32 : 16,
          },
          Input: {
            inputFontSize: getResponsiveFontSize(),
            inputFontSizeLG: getResponsiveFontSize(),
            inputFontSizeSM: getResponsiveFontSize(),
            fontSizeLG: getResponsiveFontSize(),
            fontSize: getResponsiveFontSize(),
            fontSizeSM: getResponsiveFontSize(),
            fontSizeXL: getResponsiveFontSize(),
            fontSizeIcon: getResponsiveFontSize(),
          },
          Select: {
            fontSizeLG: getResponsiveFontSize(),
            fontSize: getResponsiveFontSize(),
            fontSizeSM: getResponsiveFontSize(),
            fontSizeXL: getResponsiveFontSize(),
          },
          Dropdown: {
            fontSizeLG: getResponsiveFontSize(),
            fontSize: getResponsiveFontSize(),
            fontSizeSM: getResponsiveFontSize(),
            fontSizeXL: getResponsiveFontSize(),
          },
          Tabs: {
            fontSizeLG: getResponsiveFontSize(),
            fontSize: getResponsiveFontSize(),
            fontSizeSM: getResponsiveFontSize(),
            fontSizeXL: getResponsiveFontSize(),
          },
          DatePicker: {
            fontSizeLG: getResponsiveFontSize(),
            fontSize: getResponsiveFontSize(),
            fontSizeSM: getResponsiveFontSize(),
            fontSizeXL: getResponsiveFontSize(),
          },
          Card: {
            borderRadius: 32,
            borderRadiusOuter: 32,
            borderRadiusLG: 32,
            borderRadiusSM: 32,
            borderRadiusXS: 32,
          },
          Menu: {
            fontSizeLG: getResponsiveFontSize(),
            fontSize: getResponsiveFontSize(),
            fontSizeSM: getResponsiveFontSize(),
            fontSizeXL: getResponsiveFontSize(),
          },
        },
      }}
    >
      {isMobile ? (
        <MobileNotice />
      ) : (
        <QueryClientProvider client={queryClient}>
          <MetaContextProvider>
            <NotificationContextProvider>
              <Row className={classes.bodyWrap}>
                <Col xs={24}>
                  {isLoading ? (
                    <Loader />
                  ) : isAuthenticated ? (
                    <AuthContextProvider>
                      <ContentWrapper>{children}</ContentWrapper>
                    </AuthContextProvider>
                  ) : (
                    <MinimalLogin />
                  )}
                </Col>
              </Row>
            </NotificationContextProvider>
          </MetaContextProvider>
        </QueryClientProvider>
      )}
    </ConfigProvider>
  );
};

export default Layout;
